import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getStoreStatus } from "@/lib/storeStatus";
import { Store, MapPin, Clock, Phone, Heart, Navigation, CalendarClock } from "lucide-react";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

// Read-only preview that mirrors the client-facing store page (StorePage.jsx).
export default function StorePreviewDialog({ open, onOpenChange, form, categoryLabel }) {
  if (!form) return null;
  const cover = form.cover_photo || "";
  const todayIdx = (new Date().getDay() + 6) % 7;
  const hasHours = Array.isArray(form.hours_struct) && form.hours_struct.length > 0;
  const todayStr = new Date().toISOString().slice(0, 10);
  const fmtD = (s) => { try { return new Date(s + "T00:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "short" }); } catch (e) { return s; } };
  const specials = (form.special_hours || [])
    .filter((x) => x && x.date && (x.date_to || x.date) >= todayStr)
    .sort((a, b) => (a.date > b.date ? 1 : -1))
    .slice(0, 6);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-3xl p-0 overflow-hidden" data-testid="store-preview-dialog">
        <DialogHeader className="px-4 pt-4">
          <DialogTitle className="text-base font-extrabold">Anteprima vetrina — come ti vedono i clienti</DialogTitle>
        </DialogHeader>

        <div className="max-h-[75vh] overflow-y-auto">
          <div className="relative h-44">
            {cover
              ? <img src={cover} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full bg-gradient-to-br from-[#E63946] via-[#c92b3a] to-[#7f1d2b]" />}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>

          <div className="px-4 relative pb-5">
            <div className="-mt-10 h-20 w-20 rounded-2xl bg-white border-4 border-white shadow-lg overflow-hidden flex items-center justify-center">
              {form.logo ? <img src={form.logo} alt="" className="h-full w-full object-cover" /> : <Store className="h-8 w-8 text-slate-300" />}
            </div>
            <div className="mt-2.5">
              <h1 className="text-xl font-extrabold text-slate-900 leading-tight">{form.store_name || "Nome del negozio"}</h1>
              <p className="text-sm text-slate-500 mt-0.5">{categoryLabel || "Categoria"}</p>
            </div>

            {(() => {
              const st = getStoreStatus(form.hours_struct, form.special_hours);
              if (!st) return null;
              return (
                <div className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold ${st.open ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="preview-open-badge">
                  <span className={`h-2 w-2 rounded-full ${st.open ? "bg-emerald-500" : "bg-slate-400"}`} />
                  {st.label}{st.detail ? ` · ${st.detail}` : ""}
                </div>
              );
            })()}

            {form.description && <p className="mt-3 text-slate-600 text-sm leading-relaxed">{form.description}</p>}

            {form.gallery && form.gallery.length > 0 && (
              <div className="mt-4 -mx-4 px-4" data-testid="preview-gallery">
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                  {form.gallery.map((g, i) => (
                    <img key={i} src={g} alt="" className="h-24 w-36 object-cover rounded-xl shrink-0 border border-slate-100" />
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 grid gap-2 text-sm">
              {(form.address || form.city) && (
                <div className="flex items-center gap-2 text-slate-600"><MapPin className="h-4 w-4 text-slate-400" /> {[form.address, form.city].filter(Boolean).join(", ")}</div>
              )}
              {hasHours && (
                <div className="flex items-start gap-2 text-slate-600">
                  <Clock className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <div className="flex-1 grid grid-cols-1 gap-0.5">
                    {DAYS.map((dn, i) => {
                      const d = form.hours_struct[i] || {};
                      const ranges = (d.ranges || []).filter((r) => r && r.from);
                      const isToday = todayIdx === i;
                      return (
                        <div key={i} className={`flex justify-between gap-3 text-sm ${isToday ? "font-bold text-slate-900" : "text-slate-500"}`}>
                          <span>{dn}</span>
                          <span className="text-right">{d.closed || !ranges.length ? "Chiuso" : ranges.map((r) => `${r.from}–${r.to}`).join(", ")}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {form.phone && <div className="flex items-center gap-2 text-slate-600"><Phone className="h-4 w-4 text-slate-400" /> {form.phone}</div>}
            </div>

            {specials.length > 0 && (
              <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="preview-special-hours">
                <p className="text-xs font-bold text-amber-800 mb-1.5 flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> Orari speciali e chiusure</p>
                <div className="space-y-1">
                  {specials.map((s, i) => (
                    <div key={i} className="flex justify-between gap-3 text-sm text-amber-900">
                      <span className="truncate">{fmtD(s.date)}{s.date_to && s.date_to !== s.date ? `–${fmtD(s.date_to)}` : ""}{s.label ? ` · ${s.label}` : ""}</span>
                      <span className="shrink-0 font-semibold">{s.closed ? "Chiuso" : `${s.from}–${s.to}`}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 grid grid-cols-3 gap-2 opacity-90 pointer-events-none">
              <div className="h-11 rounded-xl kn-gradient text-white flex items-center justify-center gap-1.5 font-bold text-sm"><Heart className="h-4 w-4" /> Segui</div>
              <div className="h-11 rounded-xl border border-slate-200 flex items-center justify-center gap-1.5 font-bold text-slate-700 text-sm"><Phone className="h-4 w-4" /> Chiama</div>
              <div className="h-11 rounded-xl border border-slate-200 flex items-center justify-center gap-1.5 font-bold text-slate-700 text-sm"><Navigation className="h-4 w-4" /> Mappa</div>
            </div>
            <p className="mt-3 text-[11px] text-slate-400 text-center">Anteprima · le offerte e le recensioni appariranno qui sotto.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
